import { useEffect, useState } from "react";
import type {
  FamilyDashboardResponseDto,
  FamilyOccurrenceAbsenceDetailDto,
  FamilyScheduleItemDto,
  FamilyScheduleResponseDto,
  StudentSummaryDto,
  FamilyStudentsResponseDto,
  NotificationPrefsDto,
  NotificationsResponseDto,
  ViewerMemberCommunicationsResponseDto,
  ViewerVideosResponseDto
} from "@madamuh/domain";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  cancelFamilyOccurrenceAbsence,
  fetchFamilyDashboard,
  fetchFamilyOccurrenceAbsence,
  fetchFamilySchedule,
  fetchFamilyStudents,
  reportFamilyOccurrenceAbsence
} from "../../shared/api/family";
import {
  fetchFamilyNotifications,
  markFamilyNotificationRead,
  updateFamilyNotificationPrefs
} from "../../shared/api/notification";
import { fetchFamilyMemberCommunications } from "../../shared/api/memberCommunication";
import { fetchFamilyVideos } from "../../shared/api/video";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { canUseFamilyMode } from "../../shared/session/modeRouting";
import { NotificationCard } from "../../shared/ui/NotificationCard";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { FamilyAppShell } from "../../shared/ui/family/FamilyAppShell";
import {
  CalendarIcon,
  HomeIcon,
  MessageIcon,
  PersonIcon,
} from "../../shared/ui/icons/AppIcons";
import { MemberCommunicationCard } from "../../shared/ui/MemberCommunicationCard";
import { DEFAULT_TENANT_TIMEZONE } from "../../shared/lib/tenantDateTime";
import { GENERIC_TENANT_LABELS, getTenantLabels } from "../../shared/lib/tenantLabels";
import { describeStatus } from "../../shared/lib/uiLabels";
import {
  FamilyHomeScheduleRow,
  FamilyPersonScheduleCard,
  FamilyScheduleItemCard,
  FamilySectionHeader,
  FamilySelfNextScheduleCard,
  FamilyVideoPreviewCard,
  formatFamilyDate,
  formatFamilyDateTime,
  formatFamilyTimeRange
} from "./familyUi";

function formatDateTime(value: string): string {
  return formatFamilyDateTime(value, DEFAULT_TENANT_TIMEZONE);
}

type NotificationPrefsFormState = {
  inAppEnabled: boolean;
};

function createNotificationPrefsFormState(prefs: NotificationPrefsDto): NotificationPrefsFormState {
  return {
    inAppEnabled: prefs.inAppEnabled
  };
}

function buildStudentNameById(students: FamilyStudentsResponseDto | null): Record<string, string> {
  if (!students) {
    return {};
  }

  return Object.fromEntries(students.items.map((student) => [student.studentId, student.displayName]));
}

function FamilyAccessState({
  routeStatus,
  sessionStatus,
  session,
  errorMessage,
  hasFamilyAccessRole,
  tenantSlug,
  studentLabel
}: {
  routeStatus: "checking" | "authenticated" | "redirecting" | "error";
  sessionStatus: "idle" | "loading" | "authenticated" | "unauthenticated" | "error";
  session: ReturnType<typeof useProtectedRouteSession>["session"];
  errorMessage: string | null;
  hasFamilyAccessRole: boolean;
  tenantSlug: string;
  studentLabel: string;
}) {
  if (routeStatus === "checking" || sessionStatus === "loading") {
    return (
      <article className="family-surface family-notice">
        <p className="family-text-muted">セッションを確認しています。</p>
      </article>
    );
  }

  if (routeStatus === "redirecting") {
    return (
      <article className="family-surface family-notice">
        <p className="family-text-muted">利用開始へ移動しています。</p>
      </article>
    );
  }

  if (sessionStatus === "error" || routeStatus === "error") {
    return (
      <article className="family-surface family-notice family-notice--warning">
        <p className="family-text-muted">セッション確認に失敗しました。{errorMessage}</p>
      </article>
    );
  }

  if (routeStatus === "authenticated" && session?.accessState === "disabled") {
    return (
      <article className="family-surface family-notice family-notice--warning">
        <p className="family-text-muted">
          {session.accessMessage ?? "このアカウントは現在利用できません。管理者にお問い合わせください。"}
        </p>
      </article>
    );
  }

  if (routeStatus === "authenticated" && session?.familyAccessState === "noVisibleStudents") {
    return (
      <article className="family-surface family-notice family-notice--warning">
        <p className="family-text-muted">
          {session.familyAccessMessage ?? "現在閲覧できる生徒がいません。管理者にお問い合わせください。"}
        </p>
      </article>
    );
  }

  if (routeStatus === "authenticated" && !hasFamilyAccessRole) {
    return (
      <article className="family-surface family-notice family-notice--warning">
        <p className="family-text-muted">
          保護者または{studentLabel}本人としての利用権限が必要です。保護者の方は
          {" "}
          <Link to={`/t/${tenantSlug}/link-student`}>{studentLabel}連携</Link>
          {" "}
          へ進み、{studentLabel}本人は本人用招待をご利用ください。
        </p>
      </article>
    );
  }

  return null;
}

function findNextUpcomingItemForStudent(
  studentId: string,
  items: readonly FamilyScheduleItemDto[]
): FamilyScheduleItemDto | null {
  return items.find((item) => item.studentIds.includes(studentId)) ?? null;
}

function buildOccurrenceAbsencePath(
  tenantSlug: string,
  item: FamilyScheduleItemDto | null,
  studentId?: string
): string | null {
  if (!item || item.itemType !== "occurrence") {
    return null;
  }

  const query = studentId ? `?studentId=${encodeURIComponent(studentId)}` : "";
  return `/t/${tenantSlug}/occurrences/${item.itemId}/absence${query}`;
}

function partitionFamilyHomeStudents(studentSummaries: readonly StudentSummaryDto[]): {
  selfStudents: StudentSummaryDto[];
  familyStudents: StudentSummaryDto[];
} {
  const selfStudents = studentSummaries.filter((student) => student.relationTypes.includes("self"));
  const selfStudentIds = new Set(selfStudents.map((student) => student.studentId));
  const familyStudents = studentSummaries.filter(
    (student) =>
      !selfStudentIds.has(student.studentId)
      && (student.relationTypes.includes("parent") || student.relationTypes.includes("guardian"))
  );

  return {
    selfStudents,
    familyStudents
  };
}

export function FamilyHomePage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [dashboard, setDashboard] = useState<FamilyDashboardResponseDto | null>(null);
  const [students, setStudents] = useState<FamilyStudentsResponseDto | null>(null);
  const [communications, setCommunications] = useState<ViewerMemberCommunicationsResponseDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const hasFamilyAccessRole = canUseFamilyMode(session);
  const familyTimeZone = DEFAULT_TENANT_TIMEZONE;
  const tenantLabels = session ? getTenantLabels(session.tenant.uiLabelProfile) : GENERIC_TENANT_LABELS;

  useEffect(() => {
    if (!isSessionReady || !hasFamilyAccessRole) {
      return;
    }

    let cancelled = false;

    async function loadHome() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const [nextDashboard, nextStudents, communicationsResult] = await Promise.all([
          fetchFamilyDashboard(tenantSlug),
          fetchFamilyStudents(tenantSlug),
          fetchFamilyMemberCommunications(tenantSlug).then((value) => ({ status: "fulfilled" as const, value })).catch((reason) => ({
            status: "rejected" as const,
            reason
          }))
        ]);

        if (cancelled) {
          return;
        }

        setDashboard(nextDashboard);
        setStudents(nextStudents);
        setCommunications(communicationsResult.status === "fulfilled" ? communicationsResult.value : { items: [], unreadCount: 0 });
        if (communicationsResult.status === "rejected") {
          setLoadError("連絡事項の取得に失敗しました。予定情報は表示しています。");
        }
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (isUnauthenticatedApiError(error)) {
          navigate(`/t/${tenantSlug}/entry`, { replace: true });
          return;
        }

        setLoadState("error");
        setLoadError(getProtectedApiErrorMessage(error, "ホーム情報の取得に失敗しました"));
      }
    }

    void loadHome();

    return () => {
      cancelled = true;
    };
  }, [hasFamilyAccessRole, isSessionReady, navigate, tenantSlug]);

  const recentCommunications = communications?.items.slice(0, 6) ?? [];
  const studentSummaries = dashboard?.studentSummaries ?? students?.items ?? [];
  const { selfStudents, familyStudents } = partitionFamilyHomeStudents(studentSummaries);
  const navigationCountsSeed = {
    ...(studentSummaries.length > 0 || dashboard || students ? { home: studentSummaries.length } : {}),
    ...(dashboard ? { schedule: dashboard.upcomingItems.length } : {}),
    ...(communications ? { communications: communications.unreadCount } : {})
  };

  return (
    <FamilyAppShell
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      activeTab="home"
      navigationCountsEnabled={hasFamilyAccessRole}
      navigationCountsSeed={navigationCountsSeed}
    >
      <div className="family-page-stack">
        <FamilyAccessState
          routeStatus={routeStatus}
          sessionStatus={sessionStatus}
          session={session}
          errorMessage={errorMessage}
          hasFamilyAccessRole={hasFamilyAccessRole}
          tenantSlug={tenantSlug}
          studentLabel={tenantLabels.student}
        />

        {loadState === "loading" ? (
          <article className="family-surface family-notice">
            <p className="family-text-muted">ホーム情報を読み込んでいます。</p>
          </article>
        ) : null}
        {loadError ? (
          <article className="family-surface family-notice family-notice--warning">
            <p className="family-text-muted">{loadError}</p>
          </article>
        ) : null}

        {selfStudents.length > 0 ? (
          <section className="family-surface family-section">
            <FamilySectionHeader icon={<HomeIcon size={18} />} title="本人の次回予定" />
            <div className="family-list">
              {selfStudents.map((student) => (
                <FamilySelfNextScheduleCard
                  key={student.studentId}
                  to={
                    buildOccurrenceAbsencePath(
                      tenantSlug,
                      dashboard ? findNextUpcomingItemForStudent(student.studentId, dashboard.upcomingItems) : null,
                      student.studentId
                    ) ?? `/t/${tenantSlug}/students/${student.studentId}`
                  }
                  displayName={student.displayName}
                  nextItem={dashboard ? findNextUpcomingItemForStudent(student.studentId, dashboard.upcomingItems) : null}
                  emptyText="次回の予定はありません。"
                  showStudentName={selfStudents.length > 1}
                  timeZone={familyTimeZone}
                  labels={tenantLabels}
                />
              ))}
            </div>
          </section>
        ) : null}

        {familyStudents.length > 0 ? (
          <section className="family-surface family-section">
            <FamilySectionHeader icon={<PersonIcon size={18} />} title="家族一覧" />
            <div className="family-list">
              {familyStudents.map((student) => (
                <FamilyPersonScheduleCard
                  key={student.studentId}
                  to={`/t/${tenantSlug}/students/${student.studentId}`}
                  displayName={student.displayName}
                  profilePhoto={student.profilePhoto}
                  nextItem={dashboard ? findNextUpcomingItemForStudent(student.studentId, dashboard.upcomingItems) : null}
                  emptyText="次回の予定はありません。"
                  timeZone={familyTimeZone}
                  labels={tenantLabels}
                />
              ))}
            </div>
          </section>
        ) : null}

        {dashboard ? (
          <section className="family-surface family-section">
            <FamilySectionHeader
              icon={<CalendarIcon size={18} />}
              title="今後の予定"
              action={
                <Link className="family-section__action" to={`/t/${tenantSlug}/my-schedule`}>
                  すべて見る
                </Link>
              }
            />
            {dashboard.upcomingItems.length === 0 ? (
              <p className="family-empty-state">今後の予定はありません。</p>
            ) : (
              <div className="family-list family-list--compact">
                {dashboard.upcomingItems.slice(0, 5).map((item) => (
                  <FamilyHomeScheduleRow
                    key={`${item.itemType}:${item.itemId}`}
                    item={item}
                    to={buildOccurrenceAbsencePath(tenantSlug, item)}
                    timeZone={familyTimeZone}
                    labels={tenantLabels}
                  />
                ))}
              </div>
            )}
          </section>
        ) : null}

        {communications ? (
          <section className="family-surface family-section">
            <FamilySectionHeader
              icon={<MessageIcon size={18} />}
              title="連絡事項"
              action={
                <Link className="family-section__action" to={`/t/${tenantSlug}/member-communications`}>
                  すべて見る
                </Link>
              }
            />
            {recentCommunications.length === 0 ? (
              <p className="family-empty-state">新しい連絡事項はありません。</p>
            ) : (
              <div className="family-list family-list--compact">
                {recentCommunications.map((communication) => (
                  <MemberCommunicationCard
                    key={communication.communicationId}
                    communication={communication}
                    to={`/t/${tenantSlug}/member-communications/${communication.communicationId}`}
                    timeZone={familyTimeZone}
                    tenantSlug={tenantSlug}
                    thumbnailSurface="family"
                    enableFamilyAttachmentOpen
                    compact
                  />
                ))}
              </div>
            )}
          </section>
        ) : null}
      </div>
    </FamilyAppShell>
  );
}

export function FamilySchedulePage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [schedule, setSchedule] = useState<FamilyScheduleResponseDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const hasFamilyAccessRole = canUseFamilyMode(session);
  const familyTimeZone = DEFAULT_TENANT_TIMEZONE;
  const tenantLabels = session ? getTenantLabels(session.tenant.uiLabelProfile) : GENERIC_TENANT_LABELS;

  useEffect(() => {
    if (!isSessionReady || !hasFamilyAccessRole) {
      return;
    }

    let cancelled = false;

    async function loadSchedule() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const nextSchedule = await fetchFamilySchedule(tenantSlug);

        if (cancelled) {
          return;
        }

        setSchedule(nextSchedule);
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (isUnauthenticatedApiError(error)) {
          navigate(`/t/${tenantSlug}/entry`, { replace: true });
          return;
        }

        setLoadState("error");
        setLoadError(getProtectedApiErrorMessage(error, "スケジュールの取得に失敗しました"));
      }
    }

    void loadSchedule();

    return () => {
      cancelled = true;
    };
  }, [hasFamilyAccessRole, isSessionReady, navigate, tenantSlug]);

  return (
    <FamilyAppShell
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      activeTab="schedule"
      navigationCountsEnabled={hasFamilyAccessRole}
    >
      <div className="family-page-stack">
        <FamilyAccessState
          routeStatus={routeStatus}
          sessionStatus={sessionStatus}
          session={session}
          errorMessage={errorMessage}
          hasFamilyAccessRole={hasFamilyAccessRole}
          tenantSlug={tenantSlug}
          studentLabel={tenantLabels.student}
        />

        <section className="family-surface family-section">
          <h2 className="family-page-title">予定</h2>
          <p className="family-page-intro">今後の予定を月ごとにまとめて確認できます。</p>
        </section>

        {loadState === "loading" ? (
          <article className="family-surface family-notice">
            <p className="family-text-muted">予定を読み込んでいます。</p>
          </article>
        ) : null}
        {loadError ? (
          <article className="family-surface family-notice family-notice--warning">
            <p className="family-text-muted">{loadError}</p>
          </article>
        ) : null}

        {schedule ? (
          <>
            <article className="family-surface family-section">
              <p className="family-text-muted">
                表示範囲: {formatFamilyDate(schedule.rangeStart, familyTimeZone)} 〜 {formatFamilyDate(schedule.rangeEnd, familyTimeZone)}
              </p>
            </article>
            {schedule.monthGroups.length === 0 ? (
              <article className="family-surface family-notice">
                <p className="family-empty-state">この期間の予定はありません。</p>
              </article>
            ) : null}
            {schedule.monthGroups.map((monthGroup) => (
              <section key={monthGroup.yearMonth} className="family-surface family-section">
                <div className="family-section__header">
                  <div>
                    <h3 className="family-page-title" style={{ fontSize: "1.22rem" }}>{monthGroup.label}</h3>
                    <p className="family-page-intro">{monthGroup.items.length} 件</p>
                  </div>
                </div>
                <div className="family-list family-list--compact">
                  {monthGroup.items.map((item) => (
                    <FamilyScheduleItemCard
                      key={`${monthGroup.yearMonth}:${item.itemType}:${item.itemId}`}
                      item={item}
                      to={buildOccurrenceAbsencePath(tenantSlug, item)}
                      timeZone={familyTimeZone}
                      labels={tenantLabels}
                    />
                  ))}
                </div>
              </section>
            ))}
          </>
        ) : null}
      </div>
    </FamilyAppShell>
  );
}

export function FamilyOccurrenceAbsencePage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant", occurrenceId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [detail, setDetail] = useState<FamilyOccurrenceAbsenceDetailDto | null>(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [note, setNote] = useState("");
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const hasFamilyAccessRole = canUseFamilyMode(session);
  const tenantLabels = session ? getTenantLabels(session.tenant.uiLabelProfile) : GENERIC_TENANT_LABELS;
  const familyTimeZone = DEFAULT_TENANT_TIMEZONE;

  useEffect(() => {
    if (!isSessionReady || !hasFamilyAccessRole || !occurrenceId) {
      return;
    }

    let cancelled = false;

    async function loadAbsenceDetail() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const nextDetail = await fetchFamilyOccurrenceAbsence(tenantSlug, occurrenceId);

        if (cancelled) {
          return;
        }

        const requestedStudentId = searchParams.get("studentId");
        const initialSelected = requestedStudentId && nextDetail.students.some((student) => student.studentId === requestedStudentId)
          ? [requestedStudentId]
          : nextDetail.students.map((student) => student.studentId);

        setDetail(nextDetail);
        setSelectedStudentIds(initialSelected);
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (isUnauthenticatedApiError(error)) {
          navigate(`/t/${tenantSlug}/entry`, { replace: true });
          return;
        }

        setLoadState("error");
        setLoadError(getProtectedApiErrorMessage(error, "欠席連絡の取得に失敗しました"));
      }
    }

    void loadAbsenceDetail();

    return () => {
      cancelled = true;
    };
  }, [hasFamilyAccessRole, isSessionReady, navigate, occurrenceId, searchParams, tenantSlug]);

  function updateSelectedStudent(studentId: string, checked: boolean) {
    setSelectedStudentIds((current) =>
      checked ? [...new Set([...current, studentId])] : current.filter((item) => item !== studentId)
    );
  }

  async function handleReport() {
    if (!occurrenceId || selectedStudentIds.length === 0) {
      setSaveState("error");
      setSaveError(`${tenantLabels.student}を選択してください。`);
      return;
    }

    const selectedNames = detail?.students
      .filter((student) => selectedStudentIds.includes(student.studentId))
      .map((student) => student.displayName)
      .join(" / ") ?? "";

    if (!window.confirm(`${selectedNames} の欠席を連絡します。よろしいですか？`)) {
      return;
    }

    setSaveState("saving");
    setSaveError(null);

    try {
      const nextDetail = await reportFamilyOccurrenceAbsence(tenantSlug, occurrenceId, {
        studentIds: selectedStudentIds,
        note
      });
      setDetail(nextDetail);
      setSaveState("saved");
    } catch (error) {
      setSaveState("error");
      setSaveError(getProtectedApiErrorMessage(error, "欠席連絡に失敗しました"));
    }
  }

  async function handleCancel() {
    if (!occurrenceId || selectedStudentIds.length === 0) {
      setSaveState("error");
      setSaveError(`${tenantLabels.student}を選択してください。`);
      return;
    }

    const selectedNames = detail?.students
      .filter((student) => selectedStudentIds.includes(student.studentId))
      .map((student) => student.displayName)
      .join(" / ") ?? "";

    if (!window.confirm(`${selectedNames} の欠席連絡を取り消します。よろしいですか？`)) {
      return;
    }

    setSaveState("saving");
    setSaveError(null);

    try {
      const nextDetail = await cancelFamilyOccurrenceAbsence(tenantSlug, occurrenceId, {
        studentIds: selectedStudentIds
      });
      setDetail(nextDetail);
      setSaveState("saved");
    } catch (error) {
      setSaveState("error");
      setSaveError(getProtectedApiErrorMessage(error, "欠席連絡の取消に失敗しました"));
    }
  }

  return (
    <FamilyAppShell
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      activeTab="schedule"
      navigationCountsEnabled={hasFamilyAccessRole}
    >
      <div className="family-page-stack">
        <FamilyAccessState
          routeStatus={routeStatus}
          sessionStatus={sessionStatus}
          session={session}
          errorMessage={errorMessage}
          hasFamilyAccessRole={hasFamilyAccessRole}
          tenantSlug={tenantSlug}
          studentLabel={tenantLabels.student}
        />

        <section className="family-surface family-section">
          <h2 className="family-page-title">欠席連絡</h2>
          <p className="family-page-intro">レッスンを欠席する場合に、管理者と担当講師へ連絡できます。</p>
        </section>

        {loadState === "loading" ? (
          <article className="family-surface family-notice">
            <p className="family-text-muted">欠席連絡の状態を読み込んでいます。</p>
          </article>
        ) : null}
        {loadError ? (
          <article className="family-surface family-notice family-notice--warning">
            <p className="family-text-muted">{loadError}</p>
          </article>
        ) : null}

        {detail ? (
          <>
            <section className="family-surface family-section" style={{ display: "grid", gap: 8 }}>
              <h3 style={{ margin: 0, fontSize: 20 }}>{detail.occurrence.lessonTitle}</h3>
              <div className="family-text-muted">
                {formatFamilyDate(detail.occurrence.startAt, familyTimeZone)} {formatFamilyTimeRange(detail.occurrence.startAt, detail.occurrence.endAt, familyTimeZone)}
              </div>
              <div className="family-text-muted">{tenantLabels.studio}: {detail.occurrence.studioName ?? "未定"}</div>
              {detail.occurrence.publicNote ? <div className="family-text-muted">お知らせ: {detail.occurrence.publicNote}</div> : null}
              {!detail.canReportAbsence ? (
                <p className="family-text-muted">この予定は欠席連絡の受付対象外です。</p>
              ) : null}
            </section>

            <section className="family-surface family-section" style={{ display: "grid", gap: 12 }}>
              <h3 style={{ margin: 0, fontSize: 20 }}>対象の{tenantLabels.student}</h3>
              {detail.students.map((student) => (
                <label key={student.studentId} className="family-list-card" style={{ cursor: "pointer" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <input
                      type="checkbox"
                      checked={selectedStudentIds.includes(student.studentId)}
                      onChange={(event) => updateSelectedStudent(student.studentId, event.target.checked)}
                    />
                    <strong>{student.displayName}{student.isAbsent ? "（欠席）" : ""}</strong>
                  </span>
                  {student.isAbsent ? (
                    <span className="family-text-muted">
                      連絡済み: {student.reportedAt ? formatDateTime(student.reportedAt) : "日時未設定"}
                    </span>
                  ) : null}
                  {student.note ? <span className="family-text-muted">コメント: {student.note}</span> : null}
                </label>
              ))}

              <label style={{ display: "grid", gap: 6 }}>
                <span className="family-text-muted">コメント（任意）</span>
                <textarea
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  rows={4}
                  maxLength={500}
                  placeholder="例: 体調不良のためお休みします。"
                />
              </label>

              {saveState === "saved" ? <p className="family-empty-state">欠席連絡を更新しました。</p> : null}
              {saveError ? (
                <p className="family-text-muted" role="alert">{saveError}</p>
              ) : null}

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="family-primary-button"
                  disabled={!detail.canReportAbsence || saveState === "saving"}
                  onClick={() => void handleReport()}
                >
                  欠席を連絡する
                </button>
                <button
                  type="button"
                  className="family-secondary-button"
                  disabled={saveState === "saving"}
                  onClick={() => void handleCancel()}
                >
                  欠席連絡を取り消す
                </button>
              </div>
            </section>
          </>
        ) : null}
      </div>
    </FamilyAppShell>
  );
}

export function FamilyVideosPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [videos, setVideos] = useState<ViewerVideosResponseDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const hasFamilyAccessRole = canUseFamilyMode(session);
  const tenantLabels = session ? getTenantLabels(session.tenant.uiLabelProfile) : GENERIC_TENANT_LABELS;

  useEffect(() => {
    if (!isSessionReady || !hasFamilyAccessRole) {
      return;
    }

    let cancelled = false;

    async function loadVideos() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const nextVideos = await fetchFamilyVideos(tenantSlug);

        if (cancelled) {
          return;
        }

        setVideos(nextVideos);
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (isUnauthenticatedApiError(error)) {
          navigate(`/t/${tenantSlug}/entry`, { replace: true });
          return;
        }

        setLoadState("error");
        setLoadError(getProtectedApiErrorMessage(error, "動画一覧の取得に失敗しました"));
      }
    }

    void loadVideos();

    return () => {
      cancelled = true;
    };
  }, [hasFamilyAccessRole, isSessionReady, navigate, tenantSlug]);

  return (
    <FamilyAppShell
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      activeTab="communications"
      navigationCountsEnabled={hasFamilyAccessRole}
    >
      <div className="family-page-stack">
        <FamilyAccessState
          routeStatus={routeStatus}
          sessionStatus={sessionStatus}
          session={session}
          errorMessage={errorMessage}
          hasFamilyAccessRole={hasFamilyAccessRole}
          tenantSlug={tenantSlug}
          studentLabel={tenantLabels.student}
        />

        <section className="family-surface family-section">
          <h2 className="family-page-title">動画</h2>
          <p className="family-page-intro">連携中の{tenantLabels.student}に関連する公開動画です。</p>
        </section>

        {loadState === "loading" ? (
          <article className="family-surface family-notice">
            <p className="family-text-muted">動画一覧を読み込んでいます。</p>
          </article>
        ) : null}
        {loadError ? (
          <article className="family-surface family-notice family-notice--warning">
            <p className="family-text-muted">{loadError}</p>
          </article>
        ) : null}

        {videos?.items.length === 0 ? (
          <article className="family-surface family-notice">
            <p className="family-empty-state">視聴できる動画はまだありません。</p>
          </article>
        ) : null}

        {videos?.items.length ? (
          <section className="family-surface family-section">
            <div className="family-video-grid">
              {videos.items.map((video) => (
                <FamilyVideoPreviewCard
                  key={video.videoId}
                  tenantSlug={tenantSlug}
                  video={video}
                  timeZone={DEFAULT_TENANT_TIMEZONE}
                />
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </FamilyAppShell>
  );
}

export function NotificationsPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [notifications, setNotifications] = useState<NotificationsResponseDto | null>(null);
  const [students, setStudents] = useState<FamilyStudentsResponseDto | null>(null);
  const [prefsForm, setPrefsForm] = useState<NotificationPrefsFormState | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [markReadItemId, setMarkReadItemId] = useState<string | null>(null);
  const [markReadError, setMarkReadError] = useState<string | null>(null);
  const hasFamilyAccessRole = canUseFamilyMode(session);
  const tenantLabels = session ? getTenantLabels(session.tenant.uiLabelProfile) : GENERIC_TENANT_LABELS;

  useEffect(() => {
    if (!isSessionReady || !hasFamilyAccessRole) {
      return;
    }

    let cancelled = false;

    async function loadNotifications() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const [nextNotifications, nextStudents] = await Promise.all([
          fetchFamilyNotifications(tenantSlug),
          fetchFamilyStudents(tenantSlug)
        ]);

        if (cancelled) {
          return;
        }

        setNotifications(nextNotifications);
        setStudents(nextStudents);
        setPrefsForm(nextNotifications.prefs ? createNotificationPrefsFormState(nextNotifications.prefs) : null);
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (isUnauthenticatedApiError(error)) {
          navigate(`/t/${tenantSlug}/entry`, { replace: true });
          return;
        }

        setLoadState("error");
        setLoadError(getProtectedApiErrorMessage(error, "通知一覧の取得に失敗しました"));
      }
    }

    void loadNotifications();

    return () => {
      cancelled = true;
    };
  }, [hasFamilyAccessRole, isSessionReady, navigate, tenantSlug]);

  function updateBooleanField(field: keyof NotificationPrefsFormState, value: boolean) {
    setPrefsForm((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        [field]: value
      };
    });
  }

  async function handleSavePrefs() {
    if (!prefsForm) {
      return;
    }

    setSaveState("saving");
    setSaveError(null);

    try {
      const nextPrefs = await updateFamilyNotificationPrefs(tenantSlug, {
        inAppEnabled: prefsForm.inAppEnabled
      });

      setNotifications((current) => (current ? { ...current, prefs: nextPrefs } : current));
      setPrefsForm(createNotificationPrefsFormState(nextPrefs));
      setSaveState("saved");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setSaveState("error");
      setSaveError(getProtectedApiErrorMessage(error, "通知設定の更新に失敗しました"));
    }
  }

  async function handleMarkRead(itemId: string) {
    setMarkReadItemId(itemId);
    setMarkReadError(null);

    try {
      const updatedItem = await markFamilyNotificationRead(tenantSlug, itemId);
      setNotifications((current) => {
        if (!current) {
          return current;
        }

        return {
          ...current,
          items: current.items.map((item) => (item.itemId === updatedItem.itemId ? updatedItem : item))
        };
      });
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setMarkReadError(getProtectedApiErrorMessage(error, "通知の既読更新に失敗しました"));
    } finally {
      setMarkReadItemId(null);
    }
  }

  const studentNameById = buildStudentNameById(students);

  return (
    <FamilyAppShell
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      activeTab="notifications"
      navigationCountsEnabled={hasFamilyAccessRole}
      navigationCountsSeed={{
        ...(students ? { home: students.items.length } : {}),
        ...(notifications ? { notifications: notifications.items.filter((item) => !item.readAt).length } : {})
      }}
    >
      <div className="family-page-stack">
        <FamilyAccessState
          routeStatus={routeStatus}
          sessionStatus={sessionStatus}
          session={session}
          errorMessage={errorMessage}
          hasFamilyAccessRole={hasFamilyAccessRole}
          tenantSlug={tenantSlug}
          studentLabel={tenantLabels.student}
        />

        <section className="family-surface family-section">
          <h2 className="family-page-title">通知</h2>
          <p className="family-page-intro">家族向けのお知らせと通知設定です。</p>
        </section>

        {loadState === "loading" ? (
          <article className="family-surface family-notice">
            <p className="family-text-muted">通知一覧を読み込んでいます。</p>
          </article>
        ) : null}
        {loadError ? (
          <article className="family-surface family-notice family-notice--warning">
            <p className="family-text-muted">{loadError}</p>
          </article>
        ) : null}

        {prefsForm ? (
          <section className="family-surface family-section" style={{ display: "grid", gap: 12 }}>
            <h3 style={{ margin: 0, fontSize: 20 }}>通知設定</h3>
            <p className="family-text-muted">
              現在、一般ユーザー向け通知の正本はアプリ内通知です。メールやサービスメッセージの設定はここでは使用しません。
            </p>
            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={prefsForm.inAppEnabled}
                onChange={(event) => updateBooleanField("inAppEnabled", event.currentTarget.checked)}
              />
              アプリ内通知
            </label>
            <button type="button" onClick={() => void handleSavePrefs()} disabled={saveState === "saving"}>
              {saveState === "saving" ? "保存中..." : "通知設定を保存"}
            </button>
            {saveState === "saved" && notifications?.prefs ? (
              <p className="family-text-muted">
                更新日時: {formatDateTime(notifications.prefs.updatedAt)}
              </p>
            ) : null}
            {saveError ? <p className="family-text-muted">{saveError}</p> : null}
          </section>
        ) : null}

        <section className="family-surface family-section" style={{ display: "grid", gap: 12 }}>
          <h3 style={{ margin: 0, fontSize: 20 }}>受信トレイ</h3>
          <p className="family-text-muted">
            カテゴリ・優先度・関連する生徒・既読状態を確認できます。
          </p>
          {markReadError ? <p className="family-text-muted">{markReadError}</p> : null}
          {notifications?.items.length === 0 ? <p className="family-empty-state">通知はありません。</p> : null}
          {notifications?.items.map((item) => (
            <NotificationCard
              key={item.itemId}
              item={item}
              studentNameById={studentNameById}
              markReadEnabled
              markReadBusy={markReadItemId === item.itemId}
              onMarkRead={(nextItem) => void handleMarkRead(nextItem.itemId)}
              timeZone={DEFAULT_TENANT_TIMEZONE}
              tenantSlug={tenantSlug}
            />
          ))}
        </section>
      </div>
    </FamilyAppShell>
  );
}

export { FamilyVideoDetailPage } from "./FamilyVideoDetailPage";
export { FamilyMemberCommunicationsPage } from "./FamilyMemberCommunicationsPage";
export { FamilyMemberCommunicationDetailPage } from "./FamilyMemberCommunicationDetailPage";
export { StudentDetailPage } from "./StudentDetailPage";
